/**
 * Inventory History: view past inventory reports.
 * Card-based layout, distinct from Ministry Data Dashboard.
 * Shows latest report by default with dropdown to switch reports.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../api/client';
import PageHeader from '../components/PageHeader';
import './InventoryHistoryPage.css';

const INVENTORY_SECTION_KEYS = ['books', 'chips', 'coins'];

const InventoryHistoryPage = () => {
  const [reports, setReports] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoadError('');
      setIsLoading(true);
      try {
        const [reportsRes, configRes] = await Promise.all([
          get('/api/inventory'),
          get('/api/inventory/config'),
        ]);
        if (!reportsRes.ok) throw new Error('Failed to load reports');
        if (!configRes.ok) throw new Error('Failed to load config');
        const list = Array.isArray(reportsRes.data) ? reportsRes.data : [];
        setReports(list);
        if (list.length > 0) setSelectedId(list[0]._id);
        if (configRes.data?.sections) setConfig(configRes.data);
      } catch (err) {
        setLoadError('Could not load inventory reports. Is the backend running?');
        setReports([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const selectedReport = reports.find((r) => r._id === selectedId) || reports[0];

  const formatDate = (d) => {
    if (!d) return '';
    const x = new Date(d);
    return x.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="inventory-history">
        <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--cr-text-muted)' }}>Loading reports…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="inventory-history">
        <PageHeader title="Inventory History" section="gatherings" />
        <div style={{ padding: '1.5rem', background: 'var(--cr-bg-warm)', color: 'var(--cr-text)', borderRadius: '12px', textAlign: 'center', border: '1px solid var(--cr-border)' }}>
          {loadError}
        </div>
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <Link to="/inventory" className="inventory-history-link">← Submit New Report</Link>
        </div>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="inventory-history">
        <PageHeader title="Inventory History" section="gatherings" />
        <div className="inventory-history-empty">
          <p>No inventory reports yet.</p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.95rem' }}>Submit your first report to see it here.</p>
          <Link to="/inventory">
            <button className="inventory-history-btn inventory-history-btn-primary">Submit Report</button>
          </Link>
        </div>
      </div>
    );
  }

  const sections = config?.sections || {};

  return (
    <div className="inventory-history">
      <PageHeader title="Inventory History" section="gatherings">
        <p className="inventory-history-subtitle" style={{ marginTop: '0.25rem' }}>View past inventory reports</p>
      </PageHeader>

      <div className="inventory-history-controls">
        <label htmlFor="report-select">Report:</label>
        <select
          id="report-select"
          value={selectedId || ''}
          onChange={(e) => setSelectedId(e.target.value)}
          className="inventory-history-select"
        >
          {reports.map((r) => (
            <option key={r._id} value={r._id}>
              {formatDate(r.reportDate)}
            </option>
          ))}
        </select>
        <Link to="/inventory" className="inventory-history-link">Submit new</Link>
      </div>

      {selectedReport && (
        <div className="inventory-history-report">
          <div className="inventory-report-date">
            {formatDate(selectedReport.reportDate)}
          </div>

          {INVENTORY_SECTION_KEYS.map((key) => {
            const section = sections[key];
            if (!section?.items) return null;

            const rows = section.items.map((item) => {
              const current = Number(selectedReport[item.id] ?? 0);
              const belowMin = current < item.minStock;
              return (
                <div
                  key={item.id}
                  className={`inventory-report-row ${belowMin ? 'inventory-report-row-low' : ''}`}
                >
                  <span className="inventory-report-item">{item.name}</span>
                  <span className="inventory-report-min">Min: {item.minStock}</span>
                  <span className="inventory-report-current">
                    {current}
                    {belowMin && <span className="inventory-report-low-badge">Low</span>}
                  </span>
                </div>
              );
            });

            return (
              <div key={key} className="inventory-report-card">
                <h3 className="inventory-report-card-title">{section.label}</h3>
                <div className="inventory-report-rows">{rows}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="inventory-history-footer">
        <Link to="/inventory">Submit new report</Link>
        <span style={{ color: 'var(--cr-text-muted)' }}>•</span>
        <Link to="/gatherings">Gatherings</Link>
      </div>
    </div>
  );
};

export default InventoryHistoryPage;
